import { forwardRef, InputHTMLAttributes } from "react";
import { styled } from "styled-components";

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: string;
  error?: string;
}

const CheckboxWrapper = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;

const StyledCheckbox = styled.div<{ $checked?: boolean; $hasError?: boolean }>`
  width: 18px;
  height: 18px;
  border: 2px solid ${({ theme, $hasError }) => ($hasError ? "#ff4d4f" : theme.colors.border)};
  border-radius: 4px;
  background-color: ${({ theme, $checked }) => ($checked ? theme.colors.primary : theme.colors.white)};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.primary}33`};
  }

  ${HiddenCheckbox}:disabled + & {
    background-color: ${({ theme }) => theme.colors.grey100};
    cursor: not-allowed;
  }

  &::after {
    content: "";
    display: ${({ $checked }) => ($checked ? "block" : "none")};
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const Label = styled.span`
  color: ${({ theme }) => theme.colors.blue900};
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 14px;
  margin-top: 4px;
`;

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, checked, children, ...props }, ref) => {
    return (
      <div>
        <CheckboxWrapper>
          <HiddenCheckbox ref={ref} checked={checked} {...props} />
          <StyledCheckbox $checked={checked} $hasError={!!error} />
          {(label || children) && <Label>{label || children}</Label>}
        </CheckboxWrapper>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";
